import { createContext, ReactNode } from 'react';
import {
  AnalyticsService,
  AppnebulaFacebookService,
  AppnebulaTikTokService,
} from 'core/analytics/interfaces';
import {
  AnalyticsClientService,
  AnalyticsServerService,
  AppnebulaHttpFacebookService,
  AppnebulaHttpTikTokService,
  ConversionsAPIService,
  AmplitudeClientService,
  AmplitudeServerService,
  HttpBackendAnalyticsService,
  HttpBingAdsService,
  HttpFacebookService,
  HttpGTMService,
  HttpLtvPredictService,
  HttpPinterestService,
  HttpTikTokService,
  LtvPredictServiceWithObservability,
  TikTokServiceBuilder,
  FacebookServiceBuilder,
} from 'core/analytics/services';
import { AuthService } from 'core/auth/services';
import { BrowserAppResource } from 'core/common/observability/entities';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import {
  OTelObservabilitySystem,
  OTelObservabilitySystemBuilder,
} from 'core/common/observability/services';
import {
  AppClientEnvironment,
  AppEnvironment,
  AppServerEnvironment,
  ChannelAttributionClientService,
  ChannelAttributionServerService,
  ChannelAttributionService,
  ClarityHeatmapRecordingClientService,
  ClarityHeatmapRecordingServerService,
  CookiesService,
  createHttpClient,
  HeatmapRecordingService,
  HttpClient,
  LoggerBuilder,
  PersistentStorageClientService,
  PersistentStorageServerService,
  PersistentStorageService,
} from 'core/common/services';
import { CookiesClientService, CookiesServerService } from 'core/common/services/cookie';
import { Logger } from 'core/common/services/logger/interfaces';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { OneTrustCookieConsentService } from 'core/cookie-consent/services';
import { featureFlagsConfig } from 'core/feature-flags/config';
import {
  CookiesIdFeatureFlagsDecorator,
  GrowthBookFeatureFlags,
  QuerySettingFeatureFlagsDecorator,
} from 'core/feature-flags/services';
import { FunnelServiceBuilder } from 'core/funnel/builders';
import { ServerSideProps } from 'core/funnel/entities';
import { FunnelService } from 'core/funnel/interfaces';
import { HandlebarsTemplateEngine } from 'core/funnel/services';
import { HumanDesignReportService } from 'core/human-design/services';
import { LocalizationService } from 'core/localization/services';
import { OffersService } from 'core/offers/services';
import { PalmDetectionService } from 'core/palmistry/services';
import { PurchaseService } from 'core/payments/services';
import {
  AutocompleteService,
  CurrencyService,
  LocationClientService,
  LocationServerService,
  LocationService,
  SettingsService,
  SubscriptionsService,
  UserService,
  UtmTagsService,
} from 'core/user/services';
import { UtmTagsClientService, UtmTagsServerService } from 'core/user/services/utmTags';
import { ZodiacService } from 'core/zodiac/services';
import { AnalyticsPixelIds } from '../../analytics/entities';
import {
  ErrorMonitoring,
  ErrorMonitoringClientService,
  ErrorMonitoringServerService,
} from '../services/errorMonitoring';
import { FunnelSettingsService } from '../services/funnel-settings';
import { FunnelSettingsServiceInterface } from '../services/funnel-settings/interfaces';
import {
  GrafanaLoggerBatchDestination,
  GrafanaLoggerService,
} from '../services/grafana-logger/services';
import {
  BaseLogsBatchCache,
  LogsBatchCacheWithLocalStorage,
} from '../services/logger/batch-logger-transport';
import { Config } from './ConfigContext';

export interface Services {
  appEnvironment: AppEnvironment;
  utmTagsService: UtmTagsService;
  purchaseService: PurchaseService;
  zodiacService: ZodiacService;
  cookiesService: CookiesService;
  loggingService: Logger;
  analyticsService: AnalyticsService;
  locationService: LocationService;
  subscriptionsService: SubscriptionsService;
  settingsService: SettingsService;
  authService: AuthService;
  userService: UserService;
  autocompleteService: AutocompleteService;
  currencyService: CurrencyService;
  offersService: OffersService;
  palmDetectionService: PalmDetectionService;
  templateEngine: HandlebarsTemplateEngine;
  cookieConsentService: CookieConsentService;
  funnelService: FunnelService;
  humanDesignReportService: HumanDesignReportService;
  observabilitySystem: ObservabilitySystem;
  heatmapRecordingService: HeatmapRecordingService;
  persistentStorageService: PersistentStorageService;
  errorMonitoringService: ErrorMonitoring;
  channelAttributionService: ChannelAttributionService;
  tikTokService: AppnebulaTikTokService;
  funnelSettingsService: FunnelSettingsServiceInterface;
  facebookService: AppnebulaFacebookService;
  localizationService: LocalizationService;
  httpClient: HttpClient;
}

type AppDependencies = {
  services: Services;
  featureFlags: QuerySettingFeatureFlagsDecorator;
};

export const initServerDependencies = (
  config: Config,
  options: {
    analyticsPixelIds?: AnalyticsPixelIds;
    serverContext?: ServerSideProps;
  },
): AppDependencies => {
  const { serverContext } = options;
  const queryParams = serverContext ? serverContext.query : {};

  const cookiesService = new CookiesServerService(
    {
      domain: config.rootDomain,
    },
    serverContext,
  );

  const loggingService = new LoggerBuilder().getResult();

  const httpClient = createHttpClient(config.apiUrl);

  const observabilitySystem = new OTelObservabilitySystem(config);

  const errorMonitoringService = new ErrorMonitoringServerService();

  const utmTagsService = UtmTagsServerService.createFromQueryParams(
    cookiesService,
    loggingService,
    errorMonitoringService,
    queryParams,
  );
  const amplitudeService = new AmplitudeServerService(config.amplitudeApiKey, cookiesService);
  const analyticsService = new AnalyticsServerService(amplitudeService);
  const appEnvironment = new AppServerEnvironment();

  const growthBookFeatureFlags = new GrowthBookFeatureFlags(
    featureFlagsConfig,
    appEnvironment,
    config,
    loggingService,
  );
  const featureFlagsWithIdentify = new CookiesIdFeatureFlagsDecorator(
    growthBookFeatureFlags,
    cookiesService,
    config,
    loggingService,
  );
  const featureFlags = new QuerySettingFeatureFlagsDecorator(featureFlagsWithIdentify);
  const cookieConsentService = new OneTrustCookieConsentService(cookiesService, loggingService);
  const funnelSettingsService = new FunnelSettingsService();
  const coreFacebookService = new FacebookServiceBuilder(
    new HttpFacebookService(cookieConsentService),
  ).getResult();
  const facebookService = new AppnebulaHttpFacebookService(coreFacebookService);
  const coreTikTokService = new TikTokServiceBuilder(
    new HttpTikTokService(cookiesService, cookieConsentService),
  ).getResult();
  const tikTokService = new AppnebulaHttpTikTokService(coreTikTokService);
  const purchaseService = new PurchaseService(httpClient, loggingService, config);
  const zodiacService = new ZodiacService(httpClient, loggingService);
  const locationService = new LocationServerService(httpClient, loggingService);
  const subscriptionsService = new SubscriptionsService(httpClient, loggingService);
  const userService = new UserService(httpClient, loggingService);
  const settingsService = new SettingsService(httpClient, loggingService);
  const authService = new AuthService(httpClient, loggingService);
  const autocompleteService = new AutocompleteService(config.geocodeUrl, config.geocodeApiKey);
  const currencyService = new CurrencyService();
  const offersService = new OffersService(httpClient, loggingService);
  const palmClient = createHttpClient(config.palmApiUrl);
  const palmDetectionService = new PalmDetectionService(palmClient, loggingService);
  const templateEngine = new HandlebarsTemplateEngine();
  const funnelService = FunnelServiceBuilder.create(config);
  const humanDesignReportService = new HumanDesignReportService(httpClient, loggingService);
  const heatmapRecordingService = new ClarityHeatmapRecordingServerService();
  const persistentStorageService = new PersistentStorageServerService();
  const channelAttributionService = new ChannelAttributionServerService();
  const localizationService = new LocalizationService();

  return {
    featureFlags,
    services: {
      appEnvironment,
      loggingService,
      utmTagsService,
      purchaseService,
      zodiacService,
      cookiesService,
      analyticsService,
      locationService,
      subscriptionsService,
      settingsService,
      authService,
      userService,
      autocompleteService,
      palmDetectionService,
      currencyService,
      offersService,
      templateEngine,
      cookieConsentService,
      funnelService,
      humanDesignReportService,
      observabilitySystem,
      heatmapRecordingService,
      persistentStorageService,
      errorMonitoringService,
      channelAttributionService,
      tikTokService,
      funnelSettingsService,
      facebookService,
      localizationService,
      httpClient,
    },
  };
};

export const initClientDependencies = (
  config: Config,
  options: {
    analyticsPixelIds?: AnalyticsPixelIds;
    serverContext?: ServerSideProps;
  },
): AppDependencies => {
  const { analyticsPixelIds } = options;

  const cookiesService = new CookiesClientService({
    domain: config.rootDomain,
  });
  const appEnvironment = new AppClientEnvironment(cookiesService, config);

  const loggingService = new LoggerBuilder()
    .addConsoleTransport()
    .addBatchTransport(
      new GrafanaLoggerBatchDestination(
        new GrafanaLoggerService(createHttpClient(config.grafanaLogger.url)),
      ),
      new LogsBatchCacheWithLocalStorage(new BaseLogsBatchCache()),
    )
    .addContext({ context: config.appName })
    .getResult();
  const resource = BrowserAppResource.create();

  resource.setAppName(config.appName).setEnv(config.appEnvironment);

  const observabilitySystemBuilder = OTelObservabilitySystemBuilder.create(config)
    .addBrowserTraceProvider({ resource })
    .withSplitId(appEnvironment)
    .withHttpSpanExporter({
      url: config.observability.tracesUrl,
      headers: {},
    });

  const observabilitySystem = observabilitySystemBuilder.getResult();

  const httpClient = createHttpClient(config.apiUrl);
  // Will be replaced after the migration to the new API Gateway
  const httpGatewayClient = createHttpClient(config.apiGatewayUrl);

  const errorMonitoringService = new ErrorMonitoringClientService();
  const utmTagsService = UtmTagsClientService.createFromQueryParams(
    cookiesService,
    loggingService,
    errorMonitoringService,
  );

  const cookieConsentService = new OneTrustCookieConsentService(cookiesService, loggingService);
  const conversionsAPIService = new ConversionsAPIService(cookiesService);
  const funnelSettingsService = new FunnelSettingsService();
  const coreFacebookService = new FacebookServiceBuilder(
    new HttpFacebookService(cookieConsentService, analyticsPixelIds?.facebook.nonConditional),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const facebookService = new AppnebulaHttpFacebookService(coreFacebookService);
  const pinterestService = new HttpPinterestService(observabilitySystem);
  const gtmService = new HttpGTMService(observabilitySystem);
  const ltvPredictService = new LtvPredictServiceWithObservability(
    new HttpLtvPredictService(httpGatewayClient, loggingService, utmTagsService),
    observabilitySystem,
  );
  const amplitudeService = new AmplitudeClientService(
    observabilitySystem,
    cookiesService,
    funnelSettingsService,
    config.amplitudeApiKey,
    config.amplitudeApiEndpoint,
  );
  const backendAnalyticsService = new HttpBackendAnalyticsService(httpClient, loggingService);
  const coreTikTokService = new TikTokServiceBuilder(
    new HttpTikTokService(
      cookiesService,
      cookieConsentService,
      analyticsPixelIds?.tikTok.nonConditional,
    ),
  )
    .addMonitoring(observabilitySystem, errorMonitoringService, funnelSettingsService)
    .getResult();
  const tikTokService = new AppnebulaHttpTikTokService(coreTikTokService);

  const bingAdsService = new HttpBingAdsService(observabilitySystem);

  const analyticsService = new AnalyticsClientService(
    amplitudeService,
    conversionsAPIService,
    tikTokService,
    facebookService,
    pinterestService,
    gtmService,
    utmTagsService,
    ltvPredictService,
    backendAnalyticsService,
    loggingService,
    bingAdsService,
  );

  const growthBookFeatureFlags = new GrowthBookFeatureFlags(
    featureFlagsConfig,
    appEnvironment,
    config,
    loggingService,
  );
  const featureFlagsWithIdentify = new CookiesIdFeatureFlagsDecorator(
    growthBookFeatureFlags,
    cookiesService,
    config,
    loggingService,
  );
  const featureFlags = new QuerySettingFeatureFlagsDecorator(featureFlagsWithIdentify);

  const purchaseService = new PurchaseService(httpClient, loggingService, config);
  const zodiacService = new ZodiacService(httpClient, loggingService);
  const locationService = new LocationClientService(httpClient, loggingService);
  const subscriptionsService = new SubscriptionsService(httpClient, loggingService);
  const userService = new UserService(httpClient, loggingService);
  const settingsService = new SettingsService(httpClient, loggingService);
  const authService = new AuthService(httpClient, loggingService);
  const autocompleteService = new AutocompleteService(config.geocodeUrl, config.geocodeApiKey);
  const palmistryClient = createHttpClient(config.palmApiUrl);
  const palmDetectionService = new PalmDetectionService(palmistryClient, loggingService);
  const currencyService = new CurrencyService();
  const offersService = new OffersService(httpClient, loggingService);
  const localizationService = new LocalizationService();
  const templateEngine = new HandlebarsTemplateEngine();
  const funnelService = FunnelServiceBuilder.create(config);
  const humanDesignReportService = new HumanDesignReportService(httpClient, loggingService);
  const persistentStorageService = new PersistentStorageClientService(loggingService);
  const channelAttributionService = new ChannelAttributionClientService();
  const heatmapRecordingService = new ClarityHeatmapRecordingClientService(
    loggingService,
    analyticsService,
    cookieConsentService,
    errorMonitoringService,
  );

  return {
    featureFlags,
    services: {
      appEnvironment,
      loggingService,
      utmTagsService,
      purchaseService,
      zodiacService,
      cookiesService,
      analyticsService,
      locationService,
      subscriptionsService,
      settingsService,
      authService,
      userService,
      autocompleteService,
      palmDetectionService,
      currencyService,
      offersService,
      templateEngine,
      cookieConsentService,
      funnelService,
      humanDesignReportService,
      observabilitySystem,
      heatmapRecordingService,
      persistentStorageService,
      errorMonitoringService,
      channelAttributionService,
      tikTokService,
      funnelSettingsService,
      facebookService,
      localizationService,
      httpClient,
    },
  };
};

export const initDependencies = (
  config: Config,
  options: {
    analyticsPixelIds?: AnalyticsPixelIds;
    serverContext?: ServerSideProps;
  },
): AppDependencies => {
  const appDependencies = checkIsClientSide()
    ? initClientDependencies(config, options)
    : initServerDependencies(config, options);

  const { featureFlags, services } = appDependencies;

  featureFlags.onTrackExperiment((experiment, result) => {
    services.analyticsService.setUserExperimentGroupName(experiment.key, result.key);
    services.analyticsService.trackEvent('ab_test_split', {
      funnel_platform: 'AppNebula',
      test_name: experiment.key,
      test_group: result.key,
    });
  });

  return { ...appDependencies };
};

export const ServicesContext = createContext<Services>(undefined!);

type ServicesProviderProps = {
  value: Services;
  children: ReactNode;
};

export const ServicesProvider = ({ value, children }: ServicesProviderProps) => {
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
};
